import type {
  NotificationCategory,
  NotificationPreference,
} from '@seek/ca-graphql-schema/types';
import { Box, Card, Divider, Heading, Stack } from 'braid-design-system';
import { Fragment, type FunctionComponent } from 'react';

import type { CustomCategory } from './custom-category';
import ProductRowLink from './product-row-link';
import ProductRowToggle from './product-row-toggle';
import { useRoutes } from './routes';

type ProductCategory =
  | CustomCategory
  | Omit<NotificationPreference, 'category'>;

interface CategoryProp {
  category: NotificationCategory;
  preferences: ProductCategory[];
}

const Category: FunctionComponent<CategoryProp> = ({
  preferences,
  category,
}) => {
  const paths = useRoutes();
  return (
    <Stack space="gutter">
      <Box data-testid={`category-${category.code}`}>
        <Heading level="4">{category.description}</Heading>
      </Box>
      <Card>
        <Stack space="gutter">
          {preferences.map((x, index: number) => (
            <Fragment key={index}>
              {index > 0 ? <Divider /> : null}
              {isCustomCategory(x) ? (
                <ProductRowLink
                  key={index}
                  title={x.product.label}
                  description={x.product.descriptionJsx}
                  link={paths[x.action.link]}
                  linkText={x.action.label}
                  testId={x.testId}
                />
              ) : (
                <ProductRowToggle
                  key={index}
                  subscriptions={x.subscriptions}
                  product={x.product}
                />
              )}
            </Fragment>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
};

function isCustomCategory(
  category: ProductCategory,
): category is CustomCategory {
  return (category as CustomCategory).action !== undefined;
}

Category.defaultProps = {
  preferences: [],
};

export default Category;
